/*###########################################################################
 // Pokédex infinite scroll + region tabs
 //#########################################################################*/

let observer = null

function teardown() {
  if (observer) {
    observer.disconnect()
    observer = null
  }
}

// Lets a user leave the Pokédex list for a species page, then land back on
// the list (via the header back button or the browser's own back button)
// with all the pages they'd already lazy-loaded pre-filled and the scroll
// position restored in one jump — no replaying the incremental load.
export const POKEDEX_SCROLL_STATE_KEY = 'pokedexScrollState'
const SCROLL_STATE_KEY = POKEDEX_SCROLL_STATE_KEY

// Used by the header back button so "Back to Pokédex" returns to the exact
// filtered/searched list URL the user left, not just the bare index.
export function getSavedPokedexListUrl() {
  const saved = readPokedexScrollState()
  return saved?.url || null
}

function pokedexStateUrl(q, region) {
  const url = new URL(window.location.pathname, window.location.origin)
  if (q) url.searchParams.set('q', q)
  if (region) url.searchParams.set('region', region)
  return url.pathname + url.search
}

function savePokedexScrollState(grid) {
  try {
    sessionStorage.setItem(SCROLL_STATE_KEY, JSON.stringify({
      url: pokedexStateUrl(grid.dataset.pokedexQ || '', grid.dataset.pokedexRegion || ''),
      scrollY: window.scrollY,
      page: Number(grid.dataset.pokedexPage || '1'),
    }))
  } catch {
    // sessionStorage unavailable (private browsing, etc.) — restore just won't happen
  }
}

function readPokedexScrollState() {
  try {
    const raw = sessionStorage.getItem(SCROLL_STATE_KEY)
    return raw ? JSON.parse(raw) : null
  } catch {
    return null
  }
}

function clearPokedexScrollState() {
  try {
    sessionStorage.removeItem(SCROLL_STATE_KEY)
  } catch {
    // ignore
  }
}

function bindPokedexArtToggle(root, grid) {
  const toggleRoot = root.querySelector('[data-pokedex-art-toggle]')
  const switchBtn = toggleRoot?.querySelector('[data-pokedex-art-switch]')
  if (!toggleRoot || !switchBtn || toggleRoot.dataset.pokedexArtBound === '1') return
  toggleRoot.dataset.pokedexArtBound = '1'

  function applyStyle(style) {
    toggleRoot.dataset.artStyle = style
    grid.classList.toggle('pokedex-grid--artwork', style === 'artwork')
    switchBtn.setAttribute('aria-checked', style === 'artwork' ? 'true' : 'false')
    toggleRoot.querySelectorAll('[data-pokedex-art-label]').forEach((label) => {
      label.classList.toggle('is-active', label.dataset.pokedexArtLabel === style)
    })
  }

  applyStyle(toggleRoot.dataset.artStyle || 'sprite')

  switchBtn.addEventListener('click', async () => {
    const next = toggleRoot.dataset.artStyle === 'artwork' ? 'sprite' : 'artwork'
    applyStyle(next)

    const saveUrl = toggleRoot.dataset.saveUrl
    if (!saveUrl) return

    const body = new FormData()
    body.append(toggleRoot.dataset.csrfName || 'CRAFT_CSRF_TOKEN', toggleRoot.dataset.csrfToken || '')
    body.append('artStyle', next)

    try {
      await fetch(saveUrl, {
        method: 'POST',
        body,
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      })
    } catch (err) {
      console.warn('Could not save Pokédex artwork preference', err)
    }
  })
}

export function bindPokedexInfinite(root = document) {
  const grid = root.querySelector('[data-pokedex-grid]')
  const sentinel = root.querySelector('[data-pokedex-sentinel]')
  if (!grid || !sentinel) {
    teardown()
    return
  }

  bindPokedexArtToggle(root, grid)

  if (grid.dataset.pokedexBound === '1') return
  grid.dataset.pokedexBound = '1'

  teardown()

  const statusEl = root.querySelector('[data-pokedex-status]')
  const loadedEl = root.querySelector('[data-pokedex-loaded]')
  const totalCountEl = root.querySelector('[data-pokedex-total-count]')
  const emptyEl = root.querySelector('[data-pokedex-empty]')
  const loadingLabel = sentinel.querySelector('[data-pokedex-loading]')
  const tabs = Array.from(root.querySelectorAll('[data-pokedex-tab]'))

  let page = Number(grid.dataset.pokedexPage || '1')
  let hasMore = grid.dataset.pokedexHasMore === '1'
  let loading = false
  const moreUrl = grid.dataset.pokedexMoreUrl
  let q = grid.dataset.pokedexQ || ''
  let region = grid.dataset.pokedexRegion || ''

  function buildUrl(pageNum, forRegion = region) {
    const url = new URL(moreUrl, window.location.origin)
    url.searchParams.set('page', String(pageNum))
    if (q) url.searchParams.set('q', q)
    if (forRegion) url.searchParams.set('region', forRegion)
    return url
  }

  async function loadNext() {
    if (!hasMore || loading || !moreUrl) return
    loading = true
    if (loadingLabel) loadingLabel.hidden = false

    const nextPage = page + 1

    try {
      const res = await fetch(buildUrl(nextPage).toString(), {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const data = await res.json()
      if (!data.success) throw new Error('Bad response')

      if (data.html) {
        grid.insertAdjacentHTML('beforeend', data.html)
      }

      page = data.page
      hasMore = Boolean(data.hasMore)
      grid.dataset.pokedexPage = String(page)
      grid.dataset.pokedexHasMore = hasMore ? '1' : '0'

      if (loadedEl && typeof data.loaded === 'number') {
        loadedEl.textContent = String(data.loaded)
      }

      if (!hasMore) {
        teardown()
      }
    } catch (err) {
      console.warn('Pokédex load more failed', err)
      // Allow retry on next intersection
    } finally {
      loading = false
      if (loadingLabel) loadingLabel.hidden = true
    }
  }

  function setActiveTab(nextRegion) {
    tabs.forEach((tab) => {
      const isActive = (tab.dataset.region || '') === nextRegion
      tab.classList.toggle('is-active', isActive)
      tab.setAttribute('aria-selected', isActive ? 'true' : 'false')
    })
  }

  async function switchRegion(tab) {
    const nextRegion = tab.dataset.region || ''
    if (nextRegion === region || loading || !moreUrl) return

    loading = true
    if (loadingLabel) loadingLabel.hidden = false

    try {
      const res = await fetch(buildUrl(1, nextRegion).toString(), {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const data = await res.json()
      if (!data.success) throw new Error('Bad response')

      region = nextRegion
      page = data.page
      hasMore = Boolean(data.hasMore)
      grid.dataset.pokedexPage = String(page)
      grid.dataset.pokedexHasMore = hasMore ? '1' : '0'
      grid.dataset.pokedexRegion = region

      const hasEntries = Boolean(data.html)
      grid.innerHTML = data.html || ''
      grid.hidden = !hasEntries
      sentinel.hidden = !hasEntries
      if (statusEl) statusEl.hidden = !hasEntries

      if (emptyEl) {
        emptyEl.hidden = hasEntries
        if (!hasEntries && data.emptyHtml) {
          emptyEl.innerHTML = data.emptyHtml
        }
      }

      if (loadedEl) loadedEl.textContent = String(data.loaded ?? 0)
      if (totalCountEl) totalCountEl.textContent = String(data.total ?? 0)

      setActiveTab(region)

      if (!hasMore) {
        teardown()
      } else {
        attachObserver()
      }

      // A region switch starts a fresh list — any pending restore for the
      // previous view no longer applies.
      clearPokedexScrollState()

      history.pushState(null, '', tab.href)
    } catch (err) {
      console.warn('Pokédex region switch failed, falling back to navigation', err)
      window.location.href = tab.href
    } finally {
      loading = false
      if (loadingLabel) loadingLabel.hidden = true
    }
  }

  tabs.forEach((tab) => {
    tab.addEventListener('click', (event) => {
      event.preventDefault()
      switchRegion(tab)
    })
  })

  grid.addEventListener('click', (event) => {
    if (event.target.closest('.pokedex-tile')) savePokedexScrollState(grid)
  })

  function attachObserver() {
    if (observer) return
    observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          loadNext()
        }
      },
      { root: null, rootMargin: '400px 0px', threshold: 0 },
    )
    observer.observe(sentinel)
  }

  async function restoreScrollState() {
    const saved = readPokedexScrollState()
    if (!saved || saved.page <= 1) return

    clearPokedexScrollState()
    if (saved.url !== pokedexStateUrl(q, region)) return

    loading = true
    if (loadingLabel) loadingLabel.hidden = false

    try {
      const url = buildUrl(page)
      url.searchParams.set('restoreThrough', String(saved.page))

      const res = await fetch(url.toString(), {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const data = await res.json()
      if (!data.success) throw new Error('Bad response')

      if (data.html) grid.insertAdjacentHTML('beforeend', data.html)

      page = data.page
      hasMore = Boolean(data.hasMore)
      grid.dataset.pokedexPage = String(page)
      grid.dataset.pokedexHasMore = hasMore ? '1' : '0'

      if (loadedEl && typeof data.loaded === 'number') {
        loadedEl.textContent = String(data.loaded)
      }

      window.scrollTo({ top: saved.scrollY, left: 0, behavior: 'instant' })
    } catch (err) {
      console.warn('Pokédex scroll restore failed', err)
    } finally {
      loading = false
      if (loadingLabel) loadingLabel.hidden = true
    }
  }

  restoreScrollState().finally(() => {
    if (hasMore) attachObserver()
  })
}
